import { useState, type FormEvent } from "react";
import { Eye, EyeOff, LoaderCircle } from "lucide-react";

import type { SessionInfo } from "../../shared/contracts";
import { AuthFrame } from "../components/auth-frame";
import { Button } from "../components/ui/button";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { api, getErrorMessage } from "../lib/api";

export function SetupPage({ onComplete }: { onComplete: (session: SessionInfo) => void }) {
  const [bootstrapCode, setBootstrapCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Use at least 12 characters for your control-room password.");
      return;
    }
    if (password !== confirmation) {
      setError("The passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      onComplete(await api.setup(bootstrapCode.trim(), password));
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame title="Secure this control room" description="Use the one-time bootstrap code from your deployment, then create the password you will use to sign in.">
      <form className="grid gap-6" onSubmit={(event) => void submit(event)}>
        <FieldGroup>
          <Field data-invalid={Boolean(error) || undefined}>
            <FieldLabel htmlFor="bootstrap-code">One-time bootstrap code</FieldLabel>
            <Input
              id="bootstrap-code"
              name="bootstrapCode"
              autoComplete="one-time-code"
              value={bootstrapCode}
              onChange={(event) => setBootstrapCode(event.target.value)}
              aria-invalid={Boolean(error)}
              required
            />
            <FieldDescription>This code is used only for initial setup.</FieldDescription>
          </Field>
          <Field data-invalid={Boolean(error) || undefined}>
            <FieldLabel htmlFor="new-password">Control-room password</FieldLabel>
            <div className="relative">
              <Input
                id="new-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                className="pr-12"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-invalid={Boolean(error)}
                minLength={12}
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-xl text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            <FieldDescription>At least 12 characters. This is not your Kraken password.</FieldDescription>
          </Field>
          <Field data-invalid={Boolean(error) || undefined}>
            <FieldLabel htmlFor="confirm-password">Confirm password</FieldLabel>
            <Input
              id="confirm-password"
              name="confirmation"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              aria-invalid={Boolean(error)}
              required
            />
          </Field>
        </FieldGroup>
        {error && <FieldError>{error}</FieldError>}
        <Button type="submit" variant="signal" size="lg" disabled={busy} className="w-full">
          {busy && <LoaderCircle data-icon="inline-start" className="animate-spin" />}
          Create secure control room
        </Button>
      </form>
    </AuthFrame>
  );
}
